/**
 * Campagnes du Live School (helpers PURS).
 *
 * Une CAMPAGNE est un conteneur éditorial (titre / sous-titre / description) borné
 * dans le temps (`starts_at` → `ends_at`). Elle porte un ensemble de SLOTS
 * `(niveau, matière)`, chacun avec une CADENCE (`frequency_count` × `frequency_unit`).
 * Le seed (`live-campaign-seed`) matérialise, pour chaque slot d'une campagne
 * active, les `live_challenges` aux instants d'occurrence tombant dans l'horizon.
 *
 * Tout est déterministe et sans accès à `Date.now()` : les occurrences sont
 * ancrées sur `campaign.starts_at` (pas de dérive), les fenêtres passées en
 * argument. Miroir TS de la cadence appliquée côté serveur.
 */

export type FrequencyUnit = 'hour' | 'day' | 'week';

const UNIT_MS: Readonly<Record<FrequencyUnit, number>> = {
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
};

/** Durée (ms) d'une unité de fréquence. */
export function frequencyUnitMs(unit: FrequencyUnit): number {
  return UNIT_MS[unit];
}

/** Unités de fréquence pour les sélecteurs admin (singulier/pluriel FR). */
export const FREQUENCY_UNITS: ReadonlyArray<{
  value: FrequencyUnit;
  label: string;
  labelPlural: string;
}> = [
  { value: 'week', label: 'semaine', labelPlural: 'semaines' },
  { value: 'day', label: 'jour', labelPlural: 'jours' },
  { value: 'hour', label: 'heure', labelPlural: 'heures' },
] as const;

/** Libellé lisible d'une cadence : « chaque semaine », « tous les 2 jours »… */
export function formatFrequency(count: number, unit: FrequencyUnit): string {
  const u = FREQUENCY_UNITS.find((x) => x.value === unit);
  const n = Math.max(1, Math.floor(count));
  if (!u) return `tous les ${n}`;
  return n <= 1 ? `chaque ${u.label}` : `tous les ${n} ${u.labelPlural}`;
}

export interface CampaignWindow {
  starts_at: string;
  ends_at: string | null;
  is_active: boolean;
}

/**
 * Une campagne est ACTIVE (affichable marketing / source du seed marketing) si le
 * drapeau est levé ET l'instant `now` est dans la fenêtre `[starts_at, ends_at]`.
 * Miroir du filtre RLS/SQL `is_active and starts_at <= now and (ends_at is null or ends_at >= now)`.
 */
export function isCampaignActive(c: CampaignWindow, now: number): boolean {
  if (!c.is_active) return false;
  const start = Date.parse(c.starts_at);
  if (Number.isFinite(start) && now < start) return false;
  if (c.ends_at) {
    const end = Date.parse(c.ends_at);
    if (Number.isFinite(end) && now > end) return false;
  }
  return true;
}

/**
 * Fenêtre d'avant-première (teaser) : combien de temps AVANT `starts_at` une
 * campagne apparaît déjà sur les surfaces marketing. On tease l'événement ~1 mois
 * avant son démarrage (30 jours).
 */
export const LIVE_TEASER_LEAD_MS = 30 * 86_400_000;

/**
 * Visible sur les surfaces marketing, TEASER INCLUS : active + `now` dans
 * `[starts_at - leadMs, ends_at]`. Généralise `isCampaignActive` (= `leadMs` 0) :
 * avec le lead par défaut, une campagne s'affiche jusqu'à 1 mois avant son
 * démarrage pour teaser l'événement. Utilisé par la lecture SSR marketing (le
 * marketing lit en service_role, donc la RLS `read_active` — stricte, `starts_at
 * <= now` — n'entre pas en jeu ; l'anon ne voit toujours pas les campagnes à venir).
 */
export function isCampaignTeased(
  c: CampaignWindow,
  now: number,
  leadMs: number = LIVE_TEASER_LEAD_MS,
): boolean {
  if (!c.is_active) return false;
  const start = Date.parse(c.starts_at);
  if (Number.isFinite(start) && now < start - leadMs) return false;
  if (c.ends_at) {
    const end = Date.parse(c.ends_at);
    if (Number.isFinite(end) && now > end) return false;
  }
  return true;
}

export type CampaignLifecycle = 'upcoming' | 'active' | 'ended' | 'inactive';

/** État de cycle de vie d'une campagne (badge admin). */
export function campaignLifecycle(c: CampaignWindow, now: number): CampaignLifecycle {
  if (!c.is_active) return 'inactive';
  const start = Date.parse(c.starts_at);
  if (Number.isFinite(start) && now < start) return 'upcoming';
  if (c.ends_at) {
    const end = Date.parse(c.ends_at);
    if (Number.isFinite(end) && now > end) return 'ended';
  }
  return 'active';
}

export interface SlotCadence {
  frequency_count: number;
  frequency_unit: FrequencyUnit;
}

/**
 * Instants ISO (UTC) des défis d'un slot tombant dans la fenêtre `[from, from+horizon]`
 * (et `≤ ends_at`). Ancrés sur `campaign.starts_at`, pas = `count × unitMs`. Les
 * occurrences strictement antérieures à `from` (ou à l'ancre) ne sont jamais
 * (re)générées — pas de résurrection de lives passés. Déterministe et borné (`max`).
 */
export function slotOccurrences(
  campaign: { starts_at: string; ends_at: string | null },
  slot: SlotCadence,
  opts: { from: number; horizonMs: number; max?: number },
): string[] {
  const anchor = Date.parse(campaign.starts_at);
  if (!Number.isFinite(anchor)) return [];
  const stepMs =
    Math.max(1, Math.floor(slot.frequency_count)) * frequencyUnitMs(slot.frequency_unit);

  const endBound = campaign.ends_at ? Date.parse(campaign.ends_at) : Number.POSITIVE_INFINITY;
  const windowEnd = Math.min(opts.from + opts.horizonMs, endBound);
  const lo = Math.max(opts.from, anchor);
  if (windowEnd < lo) return [];

  // Premier k tel que anchor + k·step >= lo.
  const k0 = Math.max(0, Math.ceil((lo - anchor) / stepMs));
  const max = opts.max ?? 2000;
  const out: string[] = [];
  for (let k = k0; out.length < max; k++) {
    const t = anchor + k * stepMs;
    if (t > windowEnd) break;
    out.push(new Date(t).toISOString());
  }
  return out;
}

export interface LiveParams {
  step_count: number;
  step_duration_ms: number;
  tolerance_ms: number;
}

/** Paramètres de live effectifs d'un slot : override slot > défaut campagne. */
export function campaignSlotLiveParams(
  campaign: { step_count: number; step_duration_ms: number; tolerance_ms: number },
  slot: { step_count: number | null; step_duration_ms: number | null },
): LiveParams {
  return {
    step_count: slot.step_count ?? campaign.step_count,
    step_duration_ms: slot.step_duration_ms ?? campaign.step_duration_ms,
    tolerance_ms: campaign.tolerance_ms,
  };
}

/**
 * Date `YYYY-MM-DD` du lundi (UTC) de la semaine contenant l'instant ISO `iso`.
 * `week_start` reste l'ancre de l'IR (streak / `last_challenge_week`). Convention
 * UTC, alignée sur `nextWeekStartISO` de `schedule.ts`.
 */
export function weekStartOf(iso: string): string {
  const t = Date.parse(iso);
  const d = new Date(t);
  const deltaToMonday = (d.getUTCDay() + 6) % 7; // 0 si lundi
  const mon = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - deltaToMonday),
  );
  return mon.toISOString().slice(0, 10);
}
